import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { changePassword, login as loginRequest } from "../api/auth";
import { ApiError } from "../api/client";
import { useCurrentUser } from "../hooks/useCurrentUser";

export function ChangePasswordPage() {
  const { user, setUser } = useCurrentUser();
  const navigate = useNavigate();
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  if (!user) {
    return null;
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);

    if (newPassword !== confirmPassword) {
      setError("Новые пароли не совпадают");
      return;
    }
    if (newPassword.length < 10) {
      setError("Минимальная длина — 10 символов");
      return;
    }

    setSubmitting(true);
    try {
      await changePassword(oldPassword, newPassword);
      // Backend cleared the cookie. Re-login transparently with the new password.
      // user is guaranteed non-null here (early-return above)
      const refreshed = await loginRequest(user!.login, newPassword);
      setUser(refreshed);
      navigate("/", { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.code === "INVALID_CREDENTIALS") {
          setError("Старый пароль введён неверно");
        } else if (err.code === "PASSWORD_POLICY") {
          const detail = err.detail;
          const msg =
            detail && typeof detail === "object" && "message" in detail
              ? String((detail as { message: unknown }).message)
              : "Пароль не отвечает требованиям";
          setError(msg);
        } else {
          setError("Не удалось сменить пароль");
        }
      } else {
        setError("Сеть недоступна");
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-full items-center justify-center px-4">
      <form
        onSubmit={handleSubmit}
        className="w-full max-w-sm space-y-4 rounded-lg bg-white p-6 shadow"
      >
        <header>
          <h1 className="text-xl font-semibold text-slate-900">Смена пароля</h1>
          <p className="text-sm text-slate-500">
            Задайте свой пароль вместо временного.
          </p>
        </header>

        <div className="space-y-1">
          <label htmlFor="old" className="block text-sm font-medium text-slate-700">
            Текущий пароль
          </label>
          <input
            id="old"
            type="password"
            autoComplete="current-password"
            required
            value={oldPassword}
            onChange={(e) => setOldPassword(e.target.value)}
            className="w-full rounded border border-slate-300 px-3 py-2 text-sm focus:border-brand-accent focus:outline-none focus:ring-1 focus:ring-brand-accent"
          />
        </div>

        <div className="space-y-1">
          <label htmlFor="new" className="block text-sm font-medium text-slate-700">
            Новый пароль (мин. 10 символов, буквы + цифры)
          </label>
          <input
            id="new"
            type="password"
            autoComplete="new-password"
            required
            minLength={10}
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            className="w-full rounded border border-slate-300 px-3 py-2 text-sm focus:border-brand-accent focus:outline-none focus:ring-1 focus:ring-brand-accent"
          />
        </div>

        <div className="space-y-1">
          <label htmlFor="confirm" className="block text-sm font-medium text-slate-700">
            Повторите новый пароль
          </label>
          <input
            id="confirm"
            type="password"
            autoComplete="new-password"
            required
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            className="w-full rounded border border-slate-300 px-3 py-2 text-sm focus:border-brand-accent focus:outline-none focus:ring-1 focus:ring-brand-accent"
          />
        </div>

        {error && (
          <div role="alert" className="rounded bg-red-50 px-3 py-2 text-sm text-red-700">
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={submitting}
          className="w-full btn-primary"
        >
          {submitting ? "Сохранение…" : "Сохранить пароль"}
        </button>
      </form>
    </div>
  );
}
